import React from 'react';
import { motion } from 'framer-motion';

interface DashboardHeaderProps {
    darkMode: boolean;
    onToggleTheme: () => void;
    onNavigate: (page: string) => void;
    onToggleMobileMenu: () => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
    darkMode,
    onToggleTheme,
    onNavigate,
    onToggleMobileMenu
}) => {
    return (
        <header className="sticky top-0 z-30 h-[52px] flex items-center gap-3 px-4 bg-[var(--bg-elevated)] border-b border-[var(--border)]">
            <button 
                onClick={onToggleMobileMenu}
                className="flex md:hidden w-8 h-8 items-center justify-center rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors"
                aria-label="Меню"
            >
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <line x1="3" y1="12" x2="21" y2="12"/>
                    <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
            </button>

            <motion.div 
                className="flex items-center gap-2 cursor-pointer select-none"
                onClick={() => onNavigate('dashboard')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                <div className="w-[26px] h-[26px] rounded-md bg-gradient-to-br from-[var(--brand)] to-[#7B5FFF] flex items-center justify-center text-white text-[13px] font-semibold relative overflow-hidden">
                    S
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                </div>
                <span className="text-[15px] font-semibold tracking-[-0.3px] text-[var(--text)]">Сканта</span>
            </motion.div>

            <div className="hidden sm:flex flex-1 max-w-[320px] mx-auto items-center gap-2 px-3 py-[7px] bg-[var(--bg)] border border-[var(--border)] rounded-lg text-[var(--text-muted)] text-xs cursor-text hover:border-[var(--border-strong)] transition-colors">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <span>Найти сайт...</span>
            </div>

            <div className="flex items-center gap-1.5 ml-auto">
                <motion.button 
                    onClick={onToggleTheme}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[var(--border)] bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)] transition-all"
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.95 }}
                    title="Тема"
                >
                    {darkMode ? (
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="5"/>
                            <line x1="12" y1="1" x2="12" y2="3"/>
                            <line x1="12" y1="21" x2="12" y2="23"/>
                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                            <line x1="1" y1="12" x2="3" y2="12"/>
                            <line x1="21" y1="12" x2="23" y2="12"/>
                            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                        </svg>
                    ) : (
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                        </svg>
                    )}
                </motion.button>

                <motion.button 
                    onClick={() => onNavigate('messages')}
                    className="relative w-8 h-8 flex items-center justify-center rounded-lg border border-[var(--border)] bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)] transition-all"
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.95 }}
                    title="Уведомления"
                >
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                    <span className="absolute top-1 right-1 w-[7px] h-[7px] rounded-full bg-[var(--status-down)] border-[1.5px] border-[var(--bg-elevated)]" />
                </motion.button>

                <motion.div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-[var(--brand-dim)] border border-[var(--brand-border)] text-[var(--brand)] text-[11px] font-semibold tracking-[0.5px] cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    ТС
                </motion.div>
            </div>
        </header>
    );
};